import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API_URL } from "../utils/api";
import { ErrorScreen } from "../components/errors";
import "./PublicInspectionReport.css";

const STATUS_CONFIG = {
  pass: { label: "Pass", color: "#22c55e", bg: "#f0fdf4", icon: "✓" },
  needs_attention: { label: "Needs Attention", color: "#f59e0b", bg: "#fffbeb", icon: "!" },
  fail: { label: "Fail", color: "#ef4444", bg: "#fef2f2", icon: "✗" },
  na: { label: "N/A", color: "#94a3b8", bg: "#f8fafc", icon: "–" },
};
// An item nobody set yet (an inspection shared before it's finished).
const NOT_CHECKED = { label: "Not checked", color: "var(--text-tertiary)", bg: "var(--surface-secondary)", icon: "?" };

const PublicInspectionReport = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/public/inspection/${token}`);
        if (!res.ok) {
          if (res.status === 410) throw new Error("This inspection link has expired.");
          if (res.status === 404) throw new Error("Inspection not found.");
          throw new Error("Failed to load inspection.");
        }
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return <div className="public-inspection-loading">Loading inspection...</div>;
  }

  if (error || !data) {
    // Public-facing — no "back to workflow" button since the visitor isn't
    // logged in. They can refresh or contact the shop.
    const errStr = String(error || "");
    let code = "default";
    if (/404|not found|invalid|expired/i.test(errStr)) code = 404;
    else if (/5\d\d|server/i.test(errStr)) code = 500;
    return (
      <ErrorScreen
        code={code}
        title="Inspection unavailable"
        message="This inspection link may have expired, been revoked, or been removed. If you think this is wrong, contact the shop directly."
        secondary={{ label: "Refresh", onClick: () => window.location.reload() }}
      />
    );
  }

  const { inspection, order, vehicle } = data;
  const items = inspection.items || [];

  // Group items by status priority: fail → needs_attention → pass → na
  const priority = { fail: 0, needs_attention: 1, pass: 2, na: 3 };
  const sortedItems = [...items].sort((a, b) => {
    const ap = priority[a.status] ?? 3;
    const bp = priority[b.status] ?? 3;
    if (ap !== bp) return ap - bp;
    return (a.ordinal || 0) - (b.ordinal || 0);
  });

  const passCount = items.filter((i) => i.status === "pass").length;
  const attentionCount = items.filter((i) => i.status === "needs_attention").length;
  const failCount = items.filter((i) => i.status === "fail").length;
  const naCount = items.filter((i) => i.status === "na").length;

  const aiSummary = inspection.metadata?.ai_summary;

  return (
    <div className="public-inspection-report">
      {/* Header */}
      <header className="pir-header">
        <div className="pir-header-content">
          <div className="pir-company">POSEIDON MARINE</div>
          <h1 className="pir-title">Inspection Report</h1>
          <div className="pir-subtitle">
            {inspection.completed_date && <span>{new Date(inspection.completed_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>}
            {order?.order_number && <span> · Order #{order.order_number}</span>}
          </div>
        </div>
      </header>

      <main className="pir-main">
        {/* Customer & Vessel */}
        <section className="pir-info-grid">
          {order?.customer_name && (
            <div className="pir-info-block">
              <div className="pir-info-label">Customer</div>
              <div className="pir-info-value">{order.customer_name}</div>
            </div>
          )}
          {vehicle && (
            <div className="pir-info-block">
              <div className="pir-info-label">Vessel</div>
              <div className="pir-info-value">
                {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") || vehicle.name || "—"}
              </div>
            </div>
          )}
        </section>

        {/* Inspection name + description */}
        <section className="pir-inspection-header">
          <h2 className="pir-inspection-name">{inspection.name || "Inspection"}</h2>
          {inspection.note && <p className="pir-inspection-description">{inspection.note}</p>}
        </section>

        {/* Summary cards */}
        <section className="pir-summary">
          <div className="pir-summary-card pir-summary-pass">
            <div className="pir-summary-count">{passCount}</div>
            <div className="pir-summary-label">Passed</div>
          </div>
          <div className="pir-summary-card pir-summary-attention">
            <div className="pir-summary-count">{attentionCount}</div>
            <div className="pir-summary-label">Needs Attention</div>
          </div>
          <div className="pir-summary-card pir-summary-fail">
            <div className="pir-summary-count">{failCount}</div>
            <div className="pir-summary-label">Failed</div>
          </div>
          <div className="pir-summary-card pir-summary-na">
            <div className="pir-summary-count">{naCount}</div>
            <div className="pir-summary-label">N/A</div>
          </div>
        </section>

        {/* AI summary */}
        {aiSummary?.summary && (
          <section className="pir-ai-summary">
            <div className="pir-ai-summary-label">Overview</div>
            <p className="pir-ai-summary-text">{aiSummary.summary}</p>
            {aiSummary.recommended_actions?.length > 0 && (
              <div className="pir-ai-actions">
                <strong>Recommended Actions:</strong>
                <ul>{aiSummary.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </div>
            )}
          </section>
        )}

        {/* Items */}
        <section className="pir-items">
          {sortedItems.map((item, idx) => {
            const cfg = STATUS_CONFIG[item.status] || NOT_CHECKED;
            return (
              <div key={item.id} className="pir-item">
                <div className="pir-item-header">
                  <span className="pir-item-badge" style={{ background: cfg.bg, color: cfg.color }}>
                    <span className="pir-item-badge-icon" style={{ borderColor: cfg.color }}>{cfg.icon}</span>
                    {cfg.label}
                  </span>
                  <h3 className="pir-item-name">{item.name || `Item ${idx + 1}`}</h3>
                </div>
                {item.message && <p className="pir-item-message">{item.message}</p>}
                {item.media && item.media.length > 0 && (
                  <div className="pir-item-photos">
                    {item.media.filter((m) => m.mime_type?.startsWith("image/")).map((m) => (
                      <img
                        key={m.id}
                        src={m.thumbnail_url || m.url}
                        alt={m.caption || ""}
                        className="pir-item-photo"
                        onClick={() => setLightbox(m)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* Footer */}
        <footer className="pir-footer">
          <p>Thank you for choosing Poseidon Marine.</p>
          <p>Questions? Contact us at <a href="mailto:info@poseidonmarine.com">info@poseidonmarine.com</a></p>
        </footer>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div className="pir-lightbox" onClick={() => setLightbox(null)}>
          <button className="pir-lightbox-close" onClick={() => setLightbox(null)}>&times;</button>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="pir-lightbox-img" onClick={(e) => e.stopPropagation()} />
          {lightbox.caption && <div className="pir-lightbox-caption">{lightbox.caption}</div>}
        </div>
      )}
    </div>
  );
};

export default PublicInspectionReport;
